import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton, // Added this import
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Message as MessageIcon,
  Code as CodeIcon,
  Logout as LogoutIcon,
  Telegram as TelegramIcon,
  Key as KeyIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../config';

const Sidebar = ({ open, onClose, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const drawerWidth = isMobile ? 280 : 280;

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Contacts', icon: <PeopleIcon />, path: '/contacts' },
    { text: 'Chats', icon: <ChatIcon />, path: '/chats' },
    { text: 'Messages', icon: <MessageIcon />, path: '/messages' },
    { text: 'Token Manager', icon: <KeyIcon />, path: '/token-manager' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'API Reference', icon: <CodeIcon />, path: '/api-reference' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  return (
    <Drawer
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          position: 'fixed',
          height: '100vh',
          top: 0,
          left: 0,
          zIndex: theme.zIndex.drawer + 2,
        },
      }}
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
    >
      <Toolbar>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TelegramIcon sx={{ color: 'primary.main', fontSize: isMobile ? 28 : 32 }} />
            <Box>
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                sx={{ fontWeight: 700, lineHeight: 1 }}
              >
                {config.APP_NAME}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                v{config.VERSION}
              </Typography>
            </Box>
          </Box>
          {isMobile && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            button
            selected={location.pathname === item.path}
            onClick={() => handleNavigation(item.path)}
            sx={{
              borderRadius: 1,
              mx: isMobile ? 1 : 2,
              my: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                },
              },
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon 
              sx={{ 
                color: location.pathname === item.path ? 'inherit' : 'text.secondary',
                minWidth: 40
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              primaryTypographyProps={{
                fontSize: isMobile ? '0.9rem' : '0.95rem',
                fontWeight: location.pathname === item.path ? 600 : 400,
              }}
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: isMobile ? 1 : 2, pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <ListItem
          button
          onClick={() => {
            onLogout();
            if (isMobile) onClose();
          }}
          sx={{
            borderRadius: 1,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            '&:hover': {
              bgcolor: 'error.dark',
            },
            mx: isMobile ? 1 : 0,
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <LogoutIcon fontSize={isMobile ? "small" : "medium"} />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{
              fontSize: isMobile ? '0.9rem' : '0.95rem',
              fontWeight: 500,
            }}
          />
        </ListItem>
      </Box>
    </Drawer>
  );
};

export default Sidebar;